import * as THREE from 'three';
import type { Tickable } from '../engine/Game';
import type { Terrain } from './Terrain';

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const BLOCKED = [
  { x: 0, z: -25, r: 9 },   // house
  { x: 40, z: 10, r: 8 },
  { x: -10, z: 35, r: 8 },
  { x: -40, z: -5, r: 8 },
  { x: 30, z: -25, r: 12 }, // pond
];

function isBlocked(x: number, z: number, pad = 0): boolean {
  for (const b of BLOCKED) {
    if (Math.hypot(x - b.x, z - b.z) < b.r + pad) return true;
  }
  return false;
}

export class Foliage implements Tickable {
  readonly group = new THREE.Group();

  constructor(private terrain: Terrain) {
    this.buildTrees();
    this.buildMushrooms();
    this.buildFlowers();
  }

  private buildTrees(): void {
    const rand = mulberry32(1337);
    const count = 180;
    const trunkGeo = new THREE.CylinderGeometry(0.3, 0.45, 3.0, 8);
    const trunkMat = new THREE.MeshStandardMaterial({ color: '#8a5e42', roughness: 1 });
    const leafGeo = new THREE.SphereGeometry(1.7, 12, 10);
    const leafPalette = ['#7cc98f', '#6ed49a', '#a6e0a4', '#ffc9e0'];
    const leafMats = leafPalette.map(
      (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.9 }),
    );

    const trunks = new THREE.InstancedMesh(trunkGeo, trunkMat, count);
    trunks.castShadow = true;
    trunks.receiveShadow = true;
    const leafInstancedMeshes = leafMats.map(
      (m) => new THREE.InstancedMesh(leafGeo, m, Math.ceil(count / leafMats.length) + 4),
    );
    leafInstancedMeshes.forEach((m) => { m.castShadow = true; });
    const leafIdx = new Array(leafMats.length).fill(0);

    const dummy = new THREE.Object3D();
    let placed = 0;
    for (let i = 0; i < count * 3 && placed < count; i++) {
      const x = (rand() - 0.5) * 180;
      const z = (rand() - 0.5) * 180;
      if (isBlocked(x, z, 2)) continue;
      const h = this.terrain.heightAt(x, z);
      if (h < -0.5) continue;
      const scale = 0.7 + rand() * 0.7;

      dummy.position.set(x, h + 1.5 * scale, z);
      dummy.rotation.set(0, rand() * Math.PI * 2, 0);
      dummy.scale.setScalar(scale);
      dummy.updateMatrix();
      trunks.setMatrixAt(placed, dummy.matrix);

      const leafGroupIdx = Math.floor(rand() * leafMats.length);
      dummy.position.y = h + 3.2 * scale;
      dummy.scale.setScalar(scale * (0.9 + rand() * 0.4));
      dummy.updateMatrix();
      const target = leafInstancedMeshes[leafGroupIdx];
      if (leafIdx[leafGroupIdx] < target.count) {
        target.setMatrixAt(leafIdx[leafGroupIdx]++, dummy.matrix);
      }
      placed++;
    }
    trunks.count = placed;
    trunks.instanceMatrix.needsUpdate = true;
    this.group.add(trunks);
    leafInstancedMeshes.forEach((m, i) => {
      m.count = leafIdx[i];
      m.instanceMatrix.needsUpdate = true;
      this.group.add(m);
    });
  }

  private buildMushrooms(): void {
    const rand = mulberry32(91);
    const count = 120;
    const stemGeo = new THREE.CylinderGeometry(0.08, 0.12, 0.5, 6);
    const stemMat = new THREE.MeshStandardMaterial({ color: '#fff0d7', roughness: 1 });
    const capGeo = new THREE.SphereGeometry(
      0.35, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2,
    );
    const caps = [
      new THREE.MeshStandardMaterial({ color: '#ff7aa8', roughness: 0.8 }),
      new THREE.MeshStandardMaterial({ color: '#cfbfff', roughness: 0.8 }),
      new THREE.MeshStandardMaterial({ color: '#ffd98a', roughness: 0.8 }),
    ];

    const stems = new THREE.InstancedMesh(stemGeo, stemMat, count);
    const capMeshes = caps.map((m) => new THREE.InstancedMesh(capGeo, m, Math.ceil(count / caps.length) + 2));
    capMeshes.forEach((m) => { m.castShadow = true; });
    const capIdx = new Array(caps.length).fill(0);

    const dummy = new THREE.Object3D();
    let placed = 0;
    for (let i = 0; i < count * 3 && placed < count; i++) {
      const x = (rand() - 0.5) * 170;
      const z = (rand() - 0.5) * 170;
      if (isBlocked(x, z, 1)) continue;
      const h = this.terrain.heightAt(x, z);
      if (h < -0.3) continue;
      const scale = 0.6 + rand() * 0.8;
      dummy.position.set(x, h + 0.25 * scale, z);
      dummy.scale.setScalar(scale);
      dummy.rotation.set(0, rand() * Math.PI * 2, 0);
      dummy.updateMatrix();
      stems.setMatrixAt(placed, dummy.matrix);

      dummy.position.y = h + 0.5 * scale;
      dummy.updateMatrix();
      const cidx = Math.floor(rand() * caps.length);
      if (capIdx[cidx] < capMeshes[cidx].count) {
        capMeshes[cidx].setMatrixAt(capIdx[cidx]++, dummy.matrix);
      }
      placed++;
    }
    stems.count = placed;
    stems.instanceMatrix.needsUpdate = true;
    this.group.add(stems);
    capMeshes.forEach((m, i) => {
      m.count = capIdx[i];
      m.instanceMatrix.needsUpdate = true;
      this.group.add(m);
    });
  }

  private buildFlowers(): void {
    const rand = mulberry32(7);
    const count = 240;
    const geo = new THREE.SphereGeometry(0.08, 6, 6);
    const mats = [
      new THREE.MeshStandardMaterial({ color: '#ff7aa8' }),
      new THREE.MeshStandardMaterial({ color: '#ffd98a' }),
      new THREE.MeshStandardMaterial({ color: '#cfbfff' }),
      new THREE.MeshStandardMaterial({ color: '#ffffff' }),
    ];
    const meshes = mats.map((m) => new THREE.InstancedMesh(geo, m, Math.ceil(count / mats.length) + 4));
    const idx = new Array(mats.length).fill(0);
    const dummy = new THREE.Object3D();
    let placed = 0;
    for (let i = 0; i < count * 2 && placed < count; i++) {
      const x = (rand() - 0.5) * 170;
      const z = (rand() - 0.5) * 170;
      if (isBlocked(x, z, 1)) continue;
      const h = this.terrain.heightAt(x, z);
      if (h < -0.2) continue;
      dummy.position.set(x, h + 0.08, z);
      dummy.scale.setScalar(0.8 + rand() * 0.6);
      dummy.updateMatrix();
      const c = Math.floor(rand() * mats.length);
      if (idx[c] < meshes[c].count) meshes[c].setMatrixAt(idx[c]++, dummy.matrix);
      placed++;
    }
    meshes.forEach((m, i) => {
      m.count = idx[i];
      m.instanceMatrix.needsUpdate = true;
      this.group.add(m);
    });
  }

  tick(): void {}
}
